import React, { useState } from 'react';
import WorkWsieIcon from '../../../ResultComponent/WorkWsieIcon';
import folderIcon from '../../../../assets/DasboardIcons/folder.svg';
import gridIcon from '../../../../assets/DasboardIcons/grid.png';
import { message } from 'antd';
import { useOfflineToggle } from '../../../OfflineToggle/useOfflineToggle';

const Sidebar = ({ open, onToggle, search, onSearch, onNewDocument, onSharedWithMe, activeItem, onActiveItemChange, onNewFolder, folders }) => {
  const [quickAccessOpen, setQuickAccessOpen] = useState(true);
  const [foldersOpen, setFoldersOpen] = useState(true);
  const { offline } = useOfflineToggle();

  // Server-backed actions can't run offline. Wrap their handlers so a click
  // gives clear feedback instead of silently doing nothing. "Create Document"
  // is deliberately left unwrapped — offline it creates a local presentation.
  const withOnline = (fn, verb) => (...args) => {
    if (offline) {
      message.warning(`You're offline. Connect to the internet to ${verb}.`);
      return;
    }
    fn?.(...args);
  };

  const OFFLINE_TITLE = 'Unavailable offline';
  // Nav items that need the server read as disabled offline: dimmed, with a
  // not-allowed cursor, instead of looking clickable and then warning.
  const navItemCls = (isActive) =>
    `sidebar-nav-item ${offline ? 'sidebar-nav-item-disabled' : ''} ${isActive && !offline ? 'sidebar-nav-item-active' : ''}`;

  return (
    <aside className={`sidebar ${open ? 'sidebar-open' : 'sidebar-closed'}`} style={{
      position: open ? 'fixed' : 'static',
      zIndex: open ? 30 : 'auto'
    }}>
      {/* Fixed Header Section */}
      <div className="sidebar-header">
        {/* Header: match image spacing and layout, keep current sidebar background */}
        <div className={`sidebar-header-content ${open ? 'sidebar-header-content-open' : 'sidebar-header-content-closed'}`}>
          {/* Logo: show full wordmark when open, stripes-only when collapsed */}
          <div className="sidebar-logo">
            {open ? (
              <div className="sidebar-logo-mark" role="img" aria-label="Workwise">
                <WorkWsieIcon
                  logoOnly={false}
                  horizontal
                  width="195"
                  height="52"
                  textColor="currentColor"
                />
              </div>
            ) : (
              <WorkWsieIcon
                width="30"
                height="30"
                logoOnly={true}
                textColor="#000000"
              />
            )}
          </div>
          <button onClick={onToggle} className={`sidebar-toggle-btn ${open ? 'sidebar-toggle-btn-open' : 'sidebar-toggle-btn-closed'}`} aria-label={open ? "Collapse sidebar" : "Expand sidebar"}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className={`sidebar-toggle-icon ${open ? 'sidebar-toggle-icon-open' : 'sidebar-toggle-icon-closed'}`}>
              {open ? (
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
              ) : (
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
              )}
            </svg>
          </button>
        </div>

        {open && (
          <>
            <div className="sidebar-actions">
              <button onClick={onNewDocument} className="sidebar-create-btn">
                <span className="text-lg leading-none">+</span>
                <span className="text-sm font-medium">
                  {offline ? 'Create Offline Document' : 'Create Document'}
                </span>
              </button>

              {/* Search queries the server, so offline it would silently return
                  nothing. Read-only rather than disabled: a disabled input
                  swallows clicks, and we want the click to explain itself. */}
              <div className="sidebar-search-container">
                <input
                  value={offline ? '' : search}
                  onChange={(e) => onSearch(e.target.value)}
                  readOnly={offline}
                  onClick={withOnline(null, 'search documents')}
                  title={offline ? OFFLINE_TITLE : undefined}
                  placeholder={offline ? 'Search unavailable offline' : 'Search documents...'}
                  className={`sidebar-search-input ${offline ? 'sidebar-search-input-disabled' : ''}`}
                />
                <svg className="sidebar-search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                </svg>
              </div>

              <div className="sidebar-divider"></div>
            </div>
          </>
        )}
      </div>

      {/* Scrollable Content Section */}
      {open && (
        <div className="sidebar-content">
          <nav className="sidebar-nav">
            <div className="sidebar-section-header">
              <span>Quick Access</span>
              <button onClick={() => setQuickAccessOpen(!quickAccessOpen)} className="sidebar-section-toggle">
                <svg width="20" height="20" className="sidebar-section-toggle-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  {quickAccessOpen ? (
                    <path d="M6 9l6 6 6-6" />
                  ) : (
                    <path d="M9 6l6 6-6 6" />
                  )}
                </svg>
              </button>
            </div>
            {quickAccessOpen && (
              <ul className="sidebar-nav-list">
                <li>
                  <button onClick={() => onActiveItemChange('all-documents')} className={`sidebar-nav-item ${activeItem === 'all-documents' ? 'sidebar-nav-item-active' : ''}`}>
                    <svg className="sidebar-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                    All Documents
                  </button>
                </li>
                <li>
                  <button
                    onClick={withOnline(onSharedWithMe, 'open My Drive')}
                    title={offline ? OFFLINE_TITLE : undefined}
                    className={navItemCls(activeItem === 'my-drive')}
                  >
                    <svg className="sidebar-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    My Drive
                  </button>
                </li>
              </ul>
            )}
          </nav>

          <div className="pt-4"></div>
          <button
            onClick={withOnline(onNewFolder, 'create folders')}
            title={offline ? OFFLINE_TITLE : undefined}
            className={`sidebar-create-btn ${offline ? 'sidebar-create-btn-disabled' : ''}`}
            style={{ marginTop: '0.75rem' }}
          >
            <span className="text-lg leading-none">+</span>
            <span className="text-sm font-medium">New Folder</span>
          </button>

          <div className="pt-6">
            <div className="sidebar-section-header">
              <span>Folders</span>
              <button onClick={() => setFoldersOpen(!foldersOpen)} className="sidebar-section-toggle">
                <svg width="20" height="20" className="sidebar-section-toggle-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  {foldersOpen ? (
                    <path d="M6 9l6 6 6-6" />
                  ) : (
                    <path d="M9 6l6 6-6 6" />
                  )}
                </svg>
              </button>
            </div>
            {foldersOpen && (
              <>
                <ul className="sidebar-nav-list">
                  {folders.map((folder) => (
                    <li key={folder.id}>
                      <button
                        onClick={withOnline(() => onActiveItemChange(folder.id), 'open folders')}
                        title={offline ? OFFLINE_TITLE : undefined}
                        className={navItemCls(activeItem === folder.id)}
                      >
                        <img src={folderIcon} alt="Folder" className="sidebar-nav-icon" />
                        {folder.name}
                      </button>
                    </li>
                  ))}
                </ul>
                {/* Folders are server-backed, so the list is empty offline —
                    say why instead of leaving a blank section. */}
                {folders.length === 0 && (
                  <div className="sidebar-empty-note">
                    {offline ? (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M1 1l22 22" />
                          <path d="M16.72 11.06A10.94 10.94 0 0119 12.55" />
                          <path d="M5 12.55a10.94 10.94 0 015.17-2.39" />
                          <path d="M10.71 5.05A16 16 0 0122.58 9" />
                          <path d="M1.42 9a15.91 15.91 0 014.7-2.88" />
                          <path d="M8.53 16.11a6 6 0 016.95 0" />
                          <line x1="12" y1="20" x2="12.01" y2="20" />
                        </svg>
                        <span>Folders are unavailable offline. Connect to the internet to see them.</span>
                      </>
                    ) : (
                      <span>No folders yet.</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {!open && (
        <div className="sidebar-collapsed-actions">
          {/* Collapsed icon-only quick actions to ensure visibility */}
          <div className="sidebar-collapsed-section">
            {[
              { id: 'docs', name: 'Docs', title: 'Docs', icon: (<svg className="sidebar-collapsed-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25V15A2.25 2.25 0 006.75 17.25h9.879a2.25 2.25 0 001.591-.659l1.53-1.53a2.25 2.25 0 00.659-1.591z" /></svg>) },
              { id: 'sheets', name: 'Sheets', title: 'Sheets', icon: (<svg className="sidebar-collapsed-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5M3.75 9.75h16.5M3.75 14.25h16.5M3.75 18.75h16.5" /></svg>) },
              { id: 'slides', name: 'Grid', title: 'Grid', icon: (<img src={gridIcon} alt="Grid" className="sidebar-collapsed-icon" />) },
              { id: 'vids', name: 'Vids', title: 'Vids', icon: (<svg className="sidebar-collapsed-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><polygon points="5,3 19,12 5,21 5,3" strokeWidth="2" /></svg>) },
              { id: 'forms', name: 'Forms', title: 'Forms', icon: (<svg className="sidebar-collapsed-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="4" y="4" width="16" height="16" rx="2" strokeWidth="2" /><path d="M8 8h8M8 12h8M8 16h5" strokeWidth="2" /></svg>) },
            ].map((item) => (
              <button key={item.id} className="sidebar-collapsed-btn" title={item.title}>
                {item.icon}
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;